/**
 * VÉRAULT Portal — FormPreviewModal
 *
 * Shows the advisor exactly what a client will see if they open the
 * onboarding magic link with the given set of sections. Mirrors the
 * editorial layout of the public /onboarding/:token experience —
 * sticky section rail on the left, oversized serif heading + italic
 * description on the right, field stubs with type labels.
 *
 * Read-only — purely a sanity check before generating the link.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, CheckCircle2 } from "lucide-react";

const TYPE_LABEL = {
  text: "single line",
  textarea: "long form",
  email: "email",
  phone: "phone",
  date: "date",
  select: "select one",
  multiselect: "select many",
  checkbox: "checkbox",
  document: "review & acknowledge",
  traveler_list: "traveler rows",
  number: "number",
};

function SectionView({ section, idx }) {
  if (!section) return null;
  const fields = section.fields || [];
  return (
    <div className="space-y-6" data-testid={`preview-section-${section.slug}`}>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          {String(idx + 1).padStart(2, "0")} · {section.category?.replace("_", " ") || "Section"}
        </p>
        <h3 className="mt-2 font-serif text-3xl text-[hsl(var(--foreground))] leading-[1.1]">
          {section.name}
        </h3>
        {section.description && (
          <p className="mt-3 font-accent italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-prose">
            {section.description}
          </p>
        )}
      </div>

      {section.requires_signature && (
        <div className="px-4 py-3 rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/5 text-[13px] text-[hsl(var(--foreground))]">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] mr-2">
            Requires signature
          </span>
          Typed signer name + IP captured.
        </div>
      )}

      {fields.length === 0 ? (
        <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">
          Free-form section — no fixed fields.
        </p>
      ) : (
        <div className="space-y-4">
          {fields.map((f) => (
            <div key={f.key} className="space-y-1.5" data-testid={`preview-field-${f.key}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  {f.label || f.key}
                  {f.required && <span className="text-[hsl(var(--accent-terracotta))] ml-1">*</span>}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]/70">
                  {TYPE_LABEL[f.type] || f.type}
                </span>
              </div>
              {f.type === "textarea" ? (
                <div className="w-full h-20 rounded-md bg-[hsl(var(--background-paper))]/70 border border-dashed border-[rgba(0,0,0,0.1)]" />
              ) : f.type === "document" ? (
                <div className="px-3 py-3 rounded-md bg-[hsl(var(--background-paper))]/70 border border-dashed border-[rgba(0,0,0,0.1)] font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                  Embedded document body (the latest version of {f.document_slug || "the linked document"}).
                </div>
              ) : f.type === "traveler_list" ? (
                <div className="px-3 py-2 rounded-md bg-[hsl(var(--background-paper))]/70 border border-dashed border-[rgba(0,0,0,0.1)] font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                  + Add traveler / Mirror from previous section
                </div>
              ) : f.type === "multiselect" || f.type === "select" ? (
                <div className="flex flex-wrap gap-1.5">
                  {(f.options || ["Option A", "Option B"]).slice(0, 6).map((opt) => (
                    <span key={opt} className="px-2.5 py-1 text-[11px] rounded-full bg-[hsl(var(--background-paper))]/70 border border-[rgba(0,0,0,0.06)] text-[hsl(var(--foreground-secondary))]">
                      {opt}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="w-full h-10 rounded-md bg-[hsl(var(--background-paper))]/70 border border-dashed border-[rgba(0,0,0,0.1)]" />
              )}
              {f.hint && (
                <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))]">{f.hint}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FormPreviewModal({ open, onClose, sections, selectedSlugs }) {
  const [active, setActive] = React.useState(0);
  const list = React.useMemo(() => {
    const bySlug = Object.fromEntries(sections.map((s) => [s.slug, s]));
    return (selectedSlugs || []).map((slug) => bySlug[slug]).filter(Boolean);
  }, [sections, selectedSlugs]);

  React.useEffect(() => {
    if (open) setActive(0);
  }, [open, selectedSlugs]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-stretch justify-center p-2 sm:p-6 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
          data-testid="form-preview-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl bg-[hsl(var(--background))] rounded-xl shadow-floating overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="px-6 sm:px-8 py-4 border-b border-[rgba(0,0,0,0.06)] flex items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                  Client preview
                </p>
                <h2 className="font-serif text-xl mt-0.5">What your client will see</h2>
              </div>
              <button
                onClick={onClose}
                className="size-9 rounded-full inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))]"
                data-testid="form-preview-close"
                aria-label="Close preview"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
            </div>

            {/* Body: two-pane */}
            <div className="flex-1 flex flex-col sm:flex-row min-h-0 max-h-[80vh] overflow-hidden">
              {/* Left rail — section list */}
              <aside className="sm:w-64 shrink-0 sm:border-r border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/40 px-5 py-6 overflow-y-auto">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-3">
                  {list.length} section{list.length === 1 ? "" : "s"}
                </p>
                <ol className="space-y-1">
                  {list.map((s, i) => (
                    <li key={s.slug}>
                      <button
                        onClick={() => setActive(i)}
                        data-testid={`preview-rail-${s.slug}`}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-start gap-2 ${
                          active === i
                            ? "bg-[hsl(var(--background))]"
                            : "hover:bg-[hsl(var(--background))]/60"
                        }`}
                      >
                        <span className={`mt-0.5 size-4 rounded-full inline-flex items-center justify-center text-[9px] font-mono shrink-0 ${
                          active === i
                            ? "bg-[hsl(var(--accent-gold))] text-white"
                            : "bg-[hsl(var(--background))] text-[hsl(var(--foreground-muted))] border border-[rgba(0,0,0,0.1)]"
                        }`}>
                          {i + 1}
                        </span>
                        <span className={`text-[13px] leading-snug ${active === i ? "font-serif" : ""}`}>
                          {s.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>

                <div className="mt-6 pt-5 border-t border-[rgba(0,0,0,0.06)]">
                  <p className="flex items-start gap-2 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] leading-relaxed">
                    <Eye className="size-3 mt-0.5 shrink-0 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                    Preview only — no link is sent until you confirm.
                  </p>
                </div>
              </aside>

              {/* Right pane — active section */}
              <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-8 bg-[hsl(var(--background))]">
                {list.length === 0 ? (
                  <p className="font-accent italic text-[15px] text-[hsl(var(--foreground-muted))]">
                    Select at least one section to preview.
                  </p>
                ) : (
                  <SectionView section={list[active]} idx={active} />
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 sm:px-8 py-3 border-t border-[rgba(0,0,0,0.06)] flex items-center justify-between gap-3 bg-[hsl(var(--background-paper))]/30">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                Mobile + desktop · same flow
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-[12px] font-mono uppercase tracking-[0.18em] rounded text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
                  data-testid="form-preview-dismiss"
                >
                  Back to send
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
